import React, { useEffect, useMemo, useState } from "react";
import { Container, Input, Button } from "reactstrap";
import { Breadcrumbs, Btn } from "../../../AbstractElements";
import DataTable from "react-data-table-component";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import moment from "moment";
import ExchangeService from "../../../Services/exchanges/ExchangeService";
import SpinnerLoader from "../../Loaders/Spinner";
import ExchangeRateModal from "./ExchangeRateModal";
import ExchangeRowActions from "./ExchangeRowActions";
import {
  ExchangeCurrencyCount,
  ExchangePeriodCell,
  ExchangeQuarterBadges,
  ExchangeRateChip,
  getQuartersForPeriod,
  periodMatchesQuarterFilter,
  getQuarterDateRange,
  QUARTER_TABS,
} from "./ExchangeListHelpers";
import "./exchange-list.scss";

const createEmptyRateRow = () => ({ currency: "EUR", value: "" });

const isValidRateString = (value) => {
  const normalized = String(value).trim().replace(",", ".");
  return normalized !== "" && /^\d+(?:\.\d+)?$/.test(normalized) && parseFloat(normalized) > 0;
};

const normalizeRateString = (value) => String(value).trim().replace(",", ".");

function ExchangeRateManual() {
  const [modal, setModal] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [quarterFilter, setQuarterFilter] = useState("all");
  const [periodYear, setPeriodYear] = useState(moment().year());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rateRows, setRateRows] = useState([createEmptyRateRow()]);
  const queryClient = useQueryClient();

  const { isPending, isError, data, error } = useQuery({
    queryKey: ["exchangeRates"],
    queryFn: ExchangeService.getAllExchangeRates,
  });

  const periods = data?.data ?? [];

  const addMutation = useMutation({
    mutationFn: ExchangeService.createExchangeRatePeriod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exchangeRates"] });
      toast.success("Exchange rate added successfully");
      setModal(false);
    },
    onError: (mutationError) => {
      toast.error(
        ExchangeService.extractErrorMessage(
          mutationError,
          `Failed to add exchange rate: ${mutationError.message}`
        )
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ExchangeService.updateExchangeRatePeriod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exchangeRates"] });
      toast.success("Exchange rate updated successfully");
      setModal(false);
    },
    onError: (mutationError) => {
      toast.error(
        ExchangeService.extractErrorMessage(
          mutationError,
          `Failed to update exchange rate: ${mutationError.message}`
        )
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ExchangeService.deleteExchangeRateGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exchangeRates"] });
      toast.success("Exchange rate deleted successfully");
    },
    onError: (mutationError) => {
      toast.error(
        ExchangeService.extractErrorMessage(
          mutationError,
          `Failed to delete exchange rate: ${mutationError.message}`
        )
      );
    },
  });

  const summary = useMemo(() => {
    const totalCurrencies = new Set(
      periods.flatMap((item) => Object.keys(item?.rates || {}))
    ).size;
    const activeToday = periods.filter((item) => {
      const today = moment().format("YYYY-MM-DD");
      return item.start_date <= today && item.end_date >= today;
    }).length;

    return {
      total: periods.length,
      totalCurrencies,
      activeToday,
    };
  }, [periods]);

  const filteredPeriods = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return periods.filter((row) => {
      if (!periodMatchesQuarterFilter(row.start_date, row.end_date, quarterFilter)) {
        return false;
      }

      if (!term) return true;

      const quarterText = getQuartersForPeriod(row.start_date, row.end_date)
        .map((q) => q.label)
        .join(" ")
        .toLowerCase();

      const rateText = Object.entries(row.rates || {})
        .map(([currency, value]) => `${currency} ${value}`)
        .join(" ")
        .toLowerCase();

      return (
        String(row.start_date || "").includes(term) ||
        String(row.end_date || "").includes(term) ||
        quarterText.includes(term) ||
        rateText.includes(term)
      );
    });
  }, [periods, searchTerm, quarterFilter]);

  useEffect(() => {
    if (selectedRate) {
      setStartDate(selectedRate?.start_date ? new Date(selectedRate.start_date) : null);
      setEndDate(selectedRate?.end_date ? new Date(selectedRate.end_date) : null);
      const normalizedRows = Object.entries(selectedRate?.rates || {}).map(
        ([currency, value]) => ({
          currency,
          value: value != null ? String(value) : "",
        })
      );
      setRateRows(normalizedRows.length ? normalizedRows : [createEmptyRateRow()]);
    } else {
      setStartDate(null);
      setEndDate(null);
      setRateRows([createEmptyRateRow()]);
    }
  }, [selectedRate]);

  const openAddModal = () => {
    setSelectedRate(null);
    setIsAdding(true);
    setPeriodYear(moment().year());
    setStartDate(null);
    setEndDate(null);
    setRateRows([createEmptyRateRow()]);
    setModal(true);
  };

  const applyQuarterPreset = (quarterId, year) => {
    const qNum = Number(String(quarterId).replace("Q", ""));
    const range = getQuarterDateRange(qNum, year);
    if (!range) return;
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const openEditModal = (rate) => {
    setSelectedRate(rate);
    setIsAdding(false);
    const primaryQuarter = getQuartersForPeriod(rate.start_date, rate.end_date)[0];
    setPeriodYear(primaryQuarter?.year || moment(rate.start_date).year());
    setModal(true);
  };

  const handleRateRowChange = (index, key, value) => {
    setRateRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const addRateRow = () => {
    setRateRows((prev) => [...prev, createEmptyRateRow()]);
  };

  const removeRateRow = (index) => {
    setRateRows((prev) => {
      if (prev.length === 1) return [createEmptyRateRow()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeleteRate = (group) => {
    Swal.fire({
      title: "Delete this period?",
      text: "All currency rates for this date range will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(group.entries || []);
      }
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const parsedRows = rateRows
      .filter(
        (row) =>
          row.currency && row.value !== "" && row.value !== null && row.value !== undefined
      )
      .map((row) => ({
        currency: row.currency,
        value: normalizeRateString(row.value),
      }));

    if (!parsedRows.length) {
      toast.error("Please add at least one currency rate.");
      return;
    }

    if (parsedRows.some((row) => !isValidRateString(row.value))) {
      toast.error("Each rate must be a valid positive number.");
      return;
    }

    const hasDuplicateCurrency =
      new Set(parsedRows.map((row) => row.currency)).size !== parsedRows.length;
    if (hasDuplicateCurrency) {
      toast.error("Each currency can only be added once.");
      return;
    }

    const periodPayload = {
      start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      rates: parsedRows.map((row) => ({
        currency: row.currency,
        value: row.value,
      })),
    };

    if (isAdding) {
      addMutation.mutate(periodPayload);
    } else {
      updateMutation.mutate({
        ...periodPayload,
        previousEntries: selectedRate?.entries || [],
      });
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        name: "Quarter",
        sortable: true,
        width: "120px",
        minWidth: "120px",
        selector: (row) => getQuartersForPeriod(row.start_date, row.end_date)[0]?.label || "",
        cell: (row) => (
          <ExchangeQuarterBadges startDate={row.start_date} endDate={row.end_date} />
        ),
      },
      {
        name: "Period",
        selector: (row) => row.start_date,
        sortable: true,
        minWidth: "140px",
        grow: 1.2,
        cell: (row) => (
          <ExchangePeriodCell startDate={row.start_date} endDate={row.end_date} />
        ),
      },
      {
        name: "Currencies",
        center: true,
        width: "100px",
        minWidth: "90px",
        cell: (row) => (
          <ExchangeCurrencyCount count={Object.keys(row.rates || {}).length} />
        ),
      },
      {
        name: "Rates",
        grow: 2,
        minWidth: "160px",
        cell: (row) => {
          const entries = Object.entries(row.rates || {});
          if (!entries.length) return <span className="text-muted">—</span>;
          return (
            <div className="ex-rates">
              {entries.map(([currency, value]) => (
                <ExchangeRateChip
                  key={`${row.groupKey}-${currency}`}
                  currency={currency}
                  value={value}
                />
              ))}
            </div>
          );
        },
      },
      {
        name: "Actions",
        width: "120px",
        minWidth: "120px",
        center: true,
        cell: (row) => (
          <ExchangeRowActions
            row={row}
            onEdit={openEditModal}
            onDelete={handleDeleteRate}
          />
        ),
      },
    ],
    []
  );

  if (isError) {
    return (
      <div className="alert alert-danger m-3">
        Error loading exchange rates: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        parent="Exchange"
        title="Exchanges"
        mainTitle="Exchange Rates"
        buttons={[
          <Btn
            key="add-rate"
            attrBtn={{
              size: "sm",
              color: "primary",
              onClick: openAddModal,
            }}
          >
            <i className="fa fa-plus me-1" />
            Add period
          </Btn>,
        ]}
      />

      <div className="rd-exchange-list">
        <Container fluid>
          <div className="ex-stats">
            <div className="ex-stat">
              <p className="ex-stat__label">Periods</p>
              <p className="ex-stat__value">{summary.total}</p>
            </div>
            <div className="ex-stat">
              <p className="ex-stat__label">Currencies</p>
              <p className="ex-stat__value">{summary.totalCurrencies}</p>
            </div>
            <div className="ex-stat">
              <p className="ex-stat__label">Active today</p>
              <p className="ex-stat__value">{summary.activeToday}</p>
            </div>
          </div>

          <div className="ex-toolbar">
            <div className="ex-toolbar__search">
              <Input
                type="search"
                placeholder="Search quarter, date, or rate…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="ex-quarter-tabs">
              {QUARTER_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`ex-quarter-tab ${quarterFilter === tab.id ? "ex-quarter-tab--active" : ""}`}
                  onClick={() => setQuarterFilter(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <span className="ex-toolbar__meta">
              {filteredPeriods.length} of {periods.length} period
              {periods.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="ex-panel">
            {isPending ? (
              <SpinnerLoader />
            ) : filteredPeriods.length === 0 ? (
              <div className="ex-empty">
                <i className="fa fa-exchange" />
                <h6>No exchange rates found</h6>
                <p>
                  {periods.length === 0
                    ? "Create your first exchange rate period to get started."
                    : "Try adjusting your search."}
                </p>
                {periods.length === 0 ? (
                  <Button color="primary" size="sm" onClick={openAddModal}>
                    <i className="fa fa-plus me-1" />
                    Add period
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="ex-table-wrap">
                <DataTable
                  columns={tableColumns}
                  data={filteredPeriods}
                  keyField="groupKey"
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 25, 50]}
                  highlightOnHover
                  responsive
                />
              </div>
            )}
          </div>
        </Container>
      </div>

      <ExchangeRateModal
        isOpen={modal}
        toggle={() => setModal(false)}
        isAdding={isAdding}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        periodYear={periodYear}
        onPeriodYearChange={setPeriodYear}
        onApplyQuarter={applyQuarterPreset}
        rateRows={rateRows}
        onRateRowChange={handleRateRowChange}
        onAddRateRow={addRateRow}
        onRemoveRateRow={removeRateRow}
        onSubmit={handleSubmit}
        isSaving={isAdding ? addMutation.isPending : updateMutation.isPending}
      />
    </>
  );
}

export default ExchangeRateManual;
